import React, {useCallback, useEffect, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack} from "@mui/material";
import {AsyncSelect, Button, ControlledTextField} from "../../../../../components/forms";
import {OperacionCLFBDetalleFields} from "../types/fields";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import Divider from "@mui/material/Divider";
import {
  OperacionCLFBConcentracionesFields,
  OperacionCLFBHoldingPeriodsVista,
  OperacionCLFBLimitesFields
} from "../../../types/clfb";
import {DialogProps} from "@mui/material/Dialog";
import {Control, useWatch} from "react-hook-form";
import {TextField} from "src/lib/itaptec/components_forms/src";
import {EntityWithIdAndDescriptionFields} from "../../../../../types";

interface SeccionCLFBDatosAdicionalesDialogProps extends DialogProps {
  codPais: number,
  control: Control<any>,
  onClose: () => void,
  onSubmit: () => Promise<void>,
  dialogTitle: React.ReactNode,
}

function SeccionCLFBDatosAdicionalesDialog({
                                             codPais,
                                             control,
                                             onClose,
                                             onSubmit,
                                             dialogTitle,
                                             ...dialogProps
                                           }: SeccionCLFBDatosAdicionalesDialogProps) {
  const [holdingPeriods, setHoldingPeriods] = useState<OperacionCLFBHoldingPeriodsVista[]>([])
  const watchActiveType = useWatch({control, name: OperacionCLFBDetalleFields.CodOperacionActivoTipo});
  const watchHoldingPeriodVerde = useWatch({control, name: OperacionCLFBDetalleFields.HoldingPeriodVerde});
  const watchHoldingPeriodAmarilla = useWatch({control, name: OperacionCLFBDetalleFields.HoldingPeriodAmarilla});
  const watchHoldingPeriodRoja = useWatch({control, name: OperacionCLFBDetalleFields.HoldingPeriodRoja});

  const [freeActiveTypeChoice, setFreeActiveTypeChoice] = useState(watchActiveType === null)
  const [freeSoloStopLossVerde, setFreeSoloStopLossVerde] = useState(false)
  const [freeSoloStopLossRoja, setFreeSoloStopLossRoja] = useState(false)
  const [freeSoloStopLossAmarilla, setFreeSoloStopLossAmarilla] = useState(false)

  useEffect(() => {
    HttpCacheOperacion.getHoldingPeriods(codPais).then(setHoldingPeriods)
  }, [codPais])

  const onSubmitWrapper = () => {
    return new Promise<void>((resolve, reject) => {
      onSubmit().then(resolve).catch(reject);
    });
  }

  const getLimitesAbsolutos = useCallback(() => HttpCacheOperacion.getLimitesAbsolutos(codPais)
    , [codPais])

  const getLimitesNetos = useCallback(() => HttpCacheOperacion.getLimitesNetos(codPais)
    , [codPais])

  const getActivoTipos = useCallback(() => HttpCacheOperacion.getActivoTipos(codPais)
    , [codPais])

  const getConcentraciones = useCallback(() => HttpCacheOperacion.getConcentraciones(codPais)
    , [codPais])

  useEffect(() => {
    if (watchActiveType === null) {
      setFreeActiveTypeChoice(true)
    }
  }, [watchActiveType])

  useEffect(() => {
    if (watchHoldingPeriodVerde === null) {
      setFreeSoloStopLossVerde(true)
    }
  }, [watchHoldingPeriodVerde])

  useEffect(() => {
    if (watchHoldingPeriodAmarilla === null) {
      setFreeSoloStopLossAmarilla(true)
    }
  }, [watchHoldingPeriodAmarilla])

  useEffect(() => {
    if (watchHoldingPeriodRoja === null) {
      setFreeSoloStopLossRoja(true)
    }
  }, [watchHoldingPeriodRoja])

  return (
    <Dialog
      open={dialogProps.open}
      maxWidth='xl'
      fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container spacing={4} pt={2}>
          <Grid item xs={12}>
            {
              !freeActiveTypeChoice
                ?
                <AsyncSelect
                  control={control}
                  name={OperacionCLFBDetalleFields.CodOperacionActivoTipo}
                  label='Tipo Activo'
                  fullWidth
                  nullOptionLabel={'Otro'}
                  loadOptions={getActivoTipos}
                />
                :
                <Stack direction={'row'}>
                  <TextField
                    name={OperacionCLFBDetalleFields.DescOperacionActivoTipo}
                    label='Tipo Activo'
                    control={control}
                    size='small'
                    autoFocus
                  />
                  <Button onClick={() => {
                    setFreeActiveTypeChoice(false)
                  }}>Opciones</Button>
                </Stack>

            }

          </Grid>

          <Grid item xs={12}>
            <Divider>Credit Limit</Divider>
          </Grid>

          <Grid item container spacing={4}>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                control={control}
                name={OperacionCLFBDetalleFields.CreditLimitNeto}
                label='Límite Neto'
                fullWidth
                optionPrefix={"USD "}
                selectNotNulleable
                loadOptions={getLimitesNetos}
                valueField={OperacionCLFBLimitesFields.LimiteImporte}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                control={control}
                name={OperacionCLFBDetalleFields.CreditLimitAbsoluto}
                label='Límite Absoluto'
                fullWidth
                optionPrefix={"USD "}
                selectNotNulleable
                loadOptions={getLimitesAbsolutos}
                valueField={OperacionCLFBLimitesFields.LimiteImporte}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                control={control}
                name={OperacionCLFBDetalleFields.CreditLimitConcentracionMaxima}
                label='Concentración Máxima'
                fullWidth
                optionSuffix={'%'}
                selectNotNulleable
                loadOptions={getConcentraciones}
                valueField={OperacionCLFBConcentracionesFields.ConcentracionImporte}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider>Stop Loss</Divider>
          </Grid>

          <Grid item container spacing={4}>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                control={control}
                name={OperacionCLFBDetalleFields.StopLossVerde}
                label='Verde'
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                control={control}
                name={OperacionCLFBDetalleFields.StopLossAmarilla}
                label='Amarilla'
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                control={control}
                name={OperacionCLFBDetalleFields.StopLossRoja}
                label='Roja'
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider>Holding Period</Divider>
          </Grid>

          <Grid item container spacing={4}>
            <Grid item xs={12} md={4}>
              {
                !freeSoloStopLossVerde
                  ? <AsyncSelect
                    control={control}
                    name={OperacionCLFBDetalleFields.HoldingPeriodVerde}
                    label='Verde'
                    fullWidth
                    nullOptionLabel={'Otro'}
                    optionSuffix={' días'}
                    loadOptions={() => Promise.resolve(holdingPeriods)}
                    valueField={EntityWithIdAndDescriptionFields.Description}
                  />
                  : <Stack direction={'row'}>
                    <TextField
                      name={OperacionCLFBDetalleFields.HoldingPeriodVerde}
                      label='Verde'
                      control={control}
                      size='small'
                      autoFocus
                    />
                    <Button onClick={() => {
                      setFreeSoloStopLossVerde(false)
                    }}>Opciones</Button>
                  </Stack>
              }

            </Grid>
            <Grid item xs={12} md={4}>
              {
                !freeSoloStopLossAmarilla
                  ? <AsyncSelect
                    control={control}
                    name={OperacionCLFBDetalleFields.HoldingPeriodAmarilla}
                    label='Amarilla'
                    fullWidth
                    nullOptionLabel={'Otro'}
                    optionSuffix={' días'}
                    loadOptions={() => Promise.resolve(holdingPeriods)}
                    valueField={EntityWithIdAndDescriptionFields.Description}
                  />
                  : <Stack direction={'row'}>
                    <TextField
                      name={OperacionCLFBDetalleFields.HoldingPeriodAmarilla}
                      label='Amarilla'
                      control={control}
                      size='small'
                      autoFocus
                    />
                    <Button onClick={() => {
                      setFreeSoloStopLossAmarilla(false)
                    }}>Opciones</Button>
                  </Stack>
              }

            </Grid>
            <Grid item xs={12} md={4}>
              {
                !freeSoloStopLossRoja
                  ? <AsyncSelect
                    control={control}
                    name={OperacionCLFBDetalleFields.HoldingPeriodRoja}
                    label='Verde'
                    fullWidth
                    nullOptionLabel={'Otro'}
                    optionSuffix={' días'}
                    loadOptions={() => Promise.resolve(holdingPeriods)}
                    valueField={EntityWithIdAndDescriptionFields.Description}
                  />
                  : <Stack direction={'row'}>
                    <TextField
                      name={OperacionCLFBDetalleFields.HoldingPeriodRoja}
                      label='Roja'
                      control={control}
                      size='small'
                      autoFocus
                    />
                    <Button onClick={() => {
                      setFreeSoloStopLossRoja(false)
                    }}>Opciones</Button>
                  </Stack>
              }

            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose}>
          Cancelar
        </Button>
        <Button variant='contained' color='primary' promise={onSubmitWrapper}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SeccionCLFBDatosAdicionalesDialog;